import React, { useState } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { Globe, Lock, Shield, EyeOff } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { useStructuresStore } from '../../store/structuresStore';
import { useAuthStore } from '../../store/authStore';
import { appAlert } from '../../utils/appAlert';
import type { PostVisibility } from '../../types';

interface DefaultVisibilitySectionProps {
  structureId: string;
  defaultPostVisibility?: PostVisibility;
}

const OPTIONS: { value: PostVisibility; label: string; icon: typeof Globe }[] = [
  { value: 'public', label: 'Public', icon: Globe },
  { value: 'insurance', label: 'Insurance only', icon: Shield },
  { value: 'private', label: 'Private', icon: Lock },
];

const BLURBS: Record<PostVisibility, string> = {
  public: 'Your new posts on this property start out visible to anyone.',
  insurance: 'Your new posts start out visible only to verified insurance agents.',
  private: 'Your new posts start out visible only to you.',
};

/**
 * Claimer-only control for structureClaims.defaultPostVisibility (client
 * plan: owners control visibility "for the whole property"). Sets the
 * starting value of the visibility picker when the owner posts on their
 * own property — each post can still override it.
 */
export function DefaultVisibilitySection({
  structureId,
  defaultPostVisibility,
}: DefaultVisibilitySectionProps) {
  const { colors } = useTheme();
  const userId = useAuthStore((s) => s.user?.id);
  const updateDefaultVisibility = useStructuresStore((s) => s.updateDefaultVisibility);
  const [isSaving, setIsSaving] = useState(false);
  const current = defaultPostVisibility ?? 'public';

  const handleSelect = async (value: PostVisibility) => {
    if (!userId || value === current || isSaving) return;
    setIsSaving(true);
    try {
      await updateDefaultVisibility(structureId, userId, value);
      // Claim subscription picks up the change; no local state needed.
    } catch {
      appAlert('Error', 'Failed to update default visibility.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <View
      style={[styles.container, { backgroundColor: colors.surface, borderColor: colors.border }]}>
      <View style={styles.header}>
        <EyeOff size={16} color={colors.textSecondary} />
        <Text style={[styles.title, { color: colors.text }]}>Default Post Visibility</Text>
      </View>

      <View style={styles.row}>
        {OPTIONS.map((opt) => {
          const selected = current === opt.value;
          return (
            <Pressable
              key={opt.value}
              onPress={() => handleSelect(opt.value)}
              disabled={isSaving}
              style={[
                styles.card,
                {
                  borderColor: selected ? colors.primary : colors.border,
                  backgroundColor: selected ? colors.primarySoft : 'transparent',
                },
              ]}
              accessibilityRole="radio"
              accessibilityState={{ selected, disabled: isSaving }}
              // RN-web doesn't emit aria-* from accessibilityState (see OptionsPanel)
              aria-checked={selected}>
              <opt.icon size={16} color={selected ? colors.primary : colors.textSecondary} />
              <Text style={[styles.cardLabel, { color: selected ? colors.primary : colors.text }]}>
                {opt.label}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <Text style={[styles.hint, { color: colors.textTertiary }]}>
        {BLURBS[current]} You can change it on each post.
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: StyleSheet.hairlineWidth,
    padding: SPACING.md,
    gap: SPACING.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  title: {
    ...TYPOGRAPHY.button,
  },
  row: {
    flexDirection: 'row',
    gap: SPACING.sm,
  },
  card: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.xs,
    paddingVertical: SPACING.sm,
    paddingHorizontal: SPACING.xs,
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1.5,
  },
  cardLabel: {
    ...TYPOGRAPHY.label,
  },
  hint: {
    ...TYPOGRAPHY.bodySmall,
    lineHeight: 16,
  },
});
